import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { T, M } from 'TOMA';
import s from './Crm.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { iCrmContact, iCrmContactForm } from 'services/Crm/Crm.type';

function CrmCompanyDetail(): JSX.Element {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { crmRepository } = useApiContext();
  const { t } = useTranslation();
  const [reloadContacts, setReloadContacts] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<iCrmContact | null>(
    null
  );

  const { response: company, loading: companyLoading } = useRepo(
    async () => await crmRepository.getCompany(Number(companyId)),
    [companyId]
  );

  const { response: contacts = [], loading: contactsLoading } = useRepo(
    async () => await crmRepository.getContactsByCompany(Number(companyId)),
    [companyId, reloadContacts]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<iCrmContactForm>();

  const openNewContact = (): void => {
    setEditingContact(null);
    reset({
      company_id: Number(companyId),
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      is_primary: false,
      notes: '',
    });
    setShowContactModal(true);
  };

  const openEditContact = (contact: iCrmContact): void => {
    setEditingContact(contact);
    reset({
      company_id: contact.company_id,
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      position: contact.position ?? '',
      is_primary: contact.is_primary,
      notes: contact.notes ?? '',
    });
    setShowContactModal(true);
  };

  const onSubmitContact = async (data: iCrmContactForm): Promise<void> => {
    try {
      const payload = { ...data, company_id: Number(companyId) };
      if (editingContact) {
        await crmRepository.updateContact(editingContact.id, payload);
        toast.success(t('crm:contact_updated'));
      } else {
        await crmRepository.createContact(payload);
        toast.success(t('crm:contact_created'));
      }
      setShowContactModal(false);
      setReloadContacts((e) => e + 1);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDeleteContact = async (contactId: number): Promise<void> => {
    if (!window.confirm(t('crm:delete_contact_confirm'))) return;
    try {
      await crmRepository.deleteContact(contactId);
      toast.success(t('crm:contact_deleted'));
      setReloadContacts((e) => e + 1);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <T.Container>
        <M.Loading loading={companyLoading} error={null} fullWiewport>
          <div className={s.pageHeader}>
            <h1 className={s.pageTitle}>{company?.name}</h1>
            <div className={s.toolbarGroup}>
              <button
                className={s.btnPrimary}
                onClick={() =>
                  navigate(
                    routes.crmCompanyEdit.to({
                      companyId: companyId ?? '',
                    })
                  )
                }
              >
                &#9998; {t('crm:edit_company')}
              </button>
              <button
                className={s.btnLink}
                onClick={() => navigate(routes.crmCompanies.path)}
              >
                {t('crm:back')}
              </button>
            </div>
          </div>

          {/* Company Info */}
          <div className={s.formCard}>
            <div className={s.formGrid}>
              <div className={s.formGroup}>
                <span className={s.formLabel}>{t('crm:siret')}</span>
                <span>{company?.siret ?? '-'}</span>
              </div>
              <div className={s.formGroup}>
                <span className={s.formLabel}>{t('crm:address')}</span>
                <span>
                  {[company?.address, company?.postal_code, company?.city]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </span>
              </div>
              <div className={s.formGroup}>
                <span className={s.formLabel}>{t('crm:country')}</span>
                <span>{company?.country ?? '-'}</span>
              </div>
              <div className={s.formGroup}>
                <span className={s.formLabel}>{t('crm:phone')}</span>
                <span>{company?.phone ?? '-'}</span>
              </div>
              <div className={s.formGroup}>
                <span className={s.formLabel}>{t('crm:email')}</span>
                <span>{company?.email ?? '-'}</span>
              </div>
              <div className={s.formGroup}>
                <span className={s.formLabel}>{t('crm:website')}</span>
                <span>{company?.website ?? '-'}</span>
              </div>
              {company?.notes && (
                <div className={s.formGroupFull}>
                  <span className={s.formLabel}>{t('crm:notes')}</span>
                  <span>{company.notes}</span>
                </div>
              )}
            </div>
          </div>

          {/* Contacts Section */}
          <div className={s.pageHeader}>
            <h2 className={s.pageTitle}>{t('crm:contacts')}</h2>
            <button className={s.btnPrimary} onClick={openNewContact}>
              {t('crm:new_contact')}
            </button>
          </div>

          <M.Loading loading={contactsLoading} error={null} fullWiewport>
            {contacts && contacts.length > 0 ? (
              <div className={s.tableWrap}>
                <table className={s.Table}>
                  <thead>
                    <tr>
                      <th>{t('crm:first_name')}</th>
                      <th>{t('crm:last_name')}</th>
                      <th>{t('crm:email')}</th>
                      <th>{t('crm:phone')}</th>
                      <th>{t('crm:position')}</th>
                      <th>{t('crm:is_primary')}</th>
                      <th>{t('crm:actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact: iCrmContact) => (
                      <tr key={contact.id} className={s.row}>
                        <td>
                          <div className={s.row__div}>{contact.first_name}</div>
                        </td>
                        <td>
                          <div className={s.row__div}>{contact.last_name}</div>
                        </td>
                        <td>
                          <div className={s.row__div}>
                            {contact.email ?? '-'}
                          </div>
                        </td>
                        <td>
                          <div className={s.row__div}>
                            {contact.phone ?? '-'}
                          </div>
                        </td>
                        <td>
                          <div className={s.row__div}>
                            {contact.position ?? '-'}
                          </div>
                        </td>
                        <td>
                          <div className={s.row__div}>
                            {contact.is_primary ? '\u2705' : '-'}
                          </div>
                        </td>
                        <td>
                          <div className={s.tableActions}>
                            <button
                              type="button"
                              className={s.actionBtn}
                              onClick={() => openEditContact(contact)}
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6b7280"
                                strokeWidth="2"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className={s.actionBtnDanger}
                              onClick={async () =>
                                await handleDeleteContact(contact.id)
                              }
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !contactsLoading && (
                <div className={s.emptyState}>
                  <div className={s.emptyIcon}>&#128100;</div>
                  <p className={s.emptyTitle}>{t('crm:no_contacts')}</p>
                </div>
              )
            )}
          </M.Loading>
        </M.Loading>
      </T.Container>

      {/* Contact Modal */}
      {showContactModal && (
        <div className={s.modal}>
          <div className={s.modalContent}>
            <h3 className={s.pageTitle} style={{ marginBottom: 20 }}>
              {editingContact ? t('crm:edit_contact') : t('crm:new_contact')}
            </h3>
            <form onSubmit={handleSubmit(onSubmitContact)}>
              <div className={s.formGrid}>
                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:first_name')}
                    <span className={s.required}> *</span>
                  </label>
                  <input
                    type="text"
                    {...register('first_name', { required: true })}
                    className={s.formInput}
                  />
                </div>
                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:last_name')}
                    <span className={s.required}> *</span>
                  </label>
                  <input
                    type="text"
                    {...register('last_name', { required: true })}
                    className={s.formInput}
                  />
                </div>
                <div className={s.formGroup}>
                  <label className={s.formLabel}>{t('crm:email')}</label>
                  <input
                    type="email"
                    {...register('email')}
                    className={s.formInput}
                  />
                </div>
                <div className={s.formGroup}>
                  <label className={s.formLabel}>{t('crm:phone')}</label>
                  <input
                    type="text"
                    {...register('phone')}
                    className={s.formInput}
                  />
                </div>
                <div className={s.formGroup}>
                  <label className={s.formLabel}>{t('crm:position')}</label>
                  <input
                    type="text"
                    {...register('position')}
                    className={s.formInput}
                  />
                </div>
                <div className={s.formGroup}>
                  <label
                    className={s.formLabel}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <input type="checkbox" {...register('is_primary')} />
                    {t('crm:is_primary')}
                  </label>
                </div>
                <div className={s.formGroupFull}>
                  <label className={s.formLabel}>{t('crm:notes')}</label>
                  <textarea
                    {...register('notes')}
                    rows={3}
                    className={s.formTextarea}
                  />
                </div>
              </div>
              <div className={s.formActions}>
                <button
                  type="submit"
                  className={s.btnPrimary}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '...' : t('crm:save')}
                </button>
                <button
                  type="button"
                  className={s.btnLink}
                  onClick={() => setShowContactModal(false)}
                >
                  {t('crm:cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CrmCompanyDetail;
